import React from 'react';
import {observable, action, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import store from '../../s';
import {checkout} from '../../../api';
import {CheckoutInputElement} from '../inputs/checkoutInputs';
import {openLink, Portal} from '../../../util';
import {ICheckoutMessageType, ICheckoutStep} from '../../i';
import {isCheckoutRedesign} from '../../ab-redesign';

@observer
export default class CheckoutAccount extends React.Component {
    @observable pwd: string = '';
    @observable expandSignIn: boolean;
    @observable loading: boolean = false;

    constructor(props) {
        super(props);
        this.signin = this.signin.bind(this);
        this.expandSignIn = false;
        makeObservable(this);
    }

    @action
    signin = async () => {
        this.loading = true;
        const wasAuthenticated = store.checkout.isAuthenticated;
        const success = await checkout('acct-login');
        this.loading = false;
        if (store.checkout.isMyFrames) {
            openLink('/Identity/Account/Login');
        } else if (success && !wasAuthenticated && store.checkout.isAuthenticated) {
            // The page's antiforgery token belongs to the signed-out shopper, so every later checkout request would fail with
            // a 400. Reload for a token issued to the account; the server has already moved the checkout to the next step.
            window.location.reload();
        }
    };

    componentDidMount() {
        if (Portal?.PortalID === 3) {
            store.moveStep(ICheckoutStep.oci);
        }
    }

    render() {
        const redesign = isCheckoutRedesign(store.checkout.isMyFrames);
        const headingStyle = {
            fontSize: 20,
            fontWeight: 700,
            textTransform: redesign ? ('none' as const) : ('uppercase' as const),
        };

        return (
            <div>
                {!redesign && (
                    <div
                        className="checkout-header"
                        style={{color: Portal?.PortalID === 3 ? '#33393a' : '', backgroundColor: Portal?.PortalID === 3 ? '#edece9' : ''}}
                    >
                        <span>Checkout Method</span>
                    </div>
                )}
                <div className="checkout-account">
                    <div className="row">
                        <div className="col-12 col-md-6 text-center ff">
                            <div className="option-container">
                                <div
                                    style={headingStyle}
                                    className="ff ca-heading"
                                >
                                    {redesign ? 'Guest checkout' : 'Guest Checkout'}
                                </div>
                                {!redesign && <div className="hr"></div>}
                                <div
                                    className="ca-sub"
                                    style={{paddingTop: 6}}
                                >
                                    {redesign ? 'No account needed.' : 'You do not need an account to checkout.'}
                                </div>
                                <div>
                                    {redesign ? (
                                        <ul>
                                            <li>Secure checkout</li>
                                            <li>Track your order anytime</li>
                                            <li>Option to create an account after purchase</li>
                                        </ul>
                                    ) : (
                                        <ul>
                                            <li>Pay safely with our secure guest checkout</li>
                                            <li>Easily track your order status</li>
                                            <li>If desired, you may create an account at the end of the checkout process</li>
                                        </ul>
                                    )}
                                </div>
                                <button
                                    id="checkout-as-guest"
                                    className={`btn-checkout btn-checkout-yellow${Portal?.PortalID === 3 ? ' btn-checkout-yellow-portal3' : ''} pointer ff`}
                                    onClick={() => store.moveStep(ICheckoutStep.oci)}
                                    aria-label="Checkout as Guest"
                                >
                                    Continue as Guest
                                </button>
                            </div>
                        </div>
                        <div className="col-12 col-md-6 text-center ff">
                            <div
                                className="option-container last"
                                style={{
                                    height: !redesign && store.checkout.messages.filter(e => e.msgType === ICheckoutMessageType.login).length > 0 ? '390px' : '',
                                }}
                            >
                                <div
                                    style={headingStyle}
                                    className="ff ca-heading"
                                >
                                    Sign in to your account
                                </div>
                                {!redesign && <div className="hr"></div>}
                                <div
                                    className="ca-sub"
                                    style={{paddingTop: 6}}
                                >
                                    {redesign ? 'Faster checkout with saved details.' : 'Sign in for a faster checkout and to save order history to your account'}
                                </div>
                                {redesign && (
                                    <ul>
                                        <li>View order history</li>
                                        <li>Save shipping &amp; payment info</li>
                                    </ul>
                                )}

                                <div className="text-left pad-top">
                                    <CheckoutInputElement
                                        ab={store.checkout.ab}
                                        o={store.checkout.email}
                                        type="email"
                                        // Redesign: the sign-in pair is username + current-password, which is what password managers look for.
                                        autocompleteOverride={redesign ? 'username' : undefined}
                                        autoFocus={false}
                                        required={true}
                                    />
                                </div>

                                <div className="text-left">
                                    <CheckoutInputElement
                                        ab={store.checkout.ab}
                                        o={store.checkout.pwd}
                                        type="password"
                                        autoFocus={false}
                                        required={true}
                                    />
                                </div>

                                {!this.loading &&
                                    store.checkout.messages
                                        .filter(e => e.msgType === ICheckoutMessageType.login)
                                        .map(e => (
                                            <div
                                                className="form-label"
                                                style={{color: 'red'}}
                                            >
                                                {e.msg}
                                            </div>
                                        ))}

                                <div
                                    style={{display: 'flex', justifyContent: 'space-between'}}
                                    className="member-container"
                                >
                                    {/* B stacks this row on phones (link above a full-width button), so the link must not self-centre there */}
                                    <div style={{alignSelf: redesign ? undefined : 'center', fontSize: redesign ? undefined : 13, paddingLeft: 4}}>
                                        <a
                                            href="/home/login.aspx?pt=1"
                                            className={redesign ? 'link ca-forgot' : 'link'}
                                            style={redesign ? undefined : {fontSize: 13}}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Forgot Password?
                                        </a>
                                    </div>
                                    <div>
                                        <button
                                            className={`btn-checkout btn-checkout-yellow${Portal?.PortalID === 3 ? ' btn-checkout-yellow-portal3' : ''} btn-checkout-member pointer ff`}
                                            onClick={this.signin}
                                            aria-label="Sign in"
                                        >
                                            {redesign ? 'Sign In' : 'Sign in'}
                                            {this.loading && (
                                                <span className="pad-left">
                                                    <i className="fas fa-circle-notch fa-spin fast-spin fa-checkout-button"></i>
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
